import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { createElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getMessage } from '@/shared/copy/messages';
import { setCacheNamespace } from '@/shared/data/cacheNamespace';
import { ApiError, isCookieAuthToken, SESSION_EXPIRED_EVENT } from '@/shared/data/client';
import { authApi } from './api';
import { clearAuthClientState } from './authClientState';
import { AuthContext, authBypassClerkEnabled } from './authContext';
import { emitAuthDiagnostic } from './authDiagnostics';
import {
  clearOwnedAuthBootstrapEvent,
  createAuthBootstrapOwnerId,
  markAuthBootstrapFinished,
  markAuthBootstrapStarted,
  waitForCoordinatedAuthBootstrap,
} from './bootstrapCoordination';
import { loadCurrentClerkSessionFromState, resolveClerkSignedInSignal } from './clerkSessionState';
import { clearPostAuthRedirectAttempt } from './postAuthRedirectGuard';
import {
  traceAuthRefreshCompleted,
  traceAuthRefreshFailed,
  traceAuthRefreshStarted,
  traceAuthSessionExpired,
} from './refreshTraceEvents';
import {
  AUTH_BROADCAST_KEY,
  broadcastAuthEvent,
  clearSessionExpiredFlag,
  getClerkProvisioningFlag,
  getClerkProvisioningMode,
  getE2eAccessToken,
  getManualSignOutFlag,
  parseAuthBroadcastEvent,
  getStoredSession,
  setClerkProvisioningFlag,
  setManualSignOutFlag,
  setSessionExpiredFlag,
  setSignOutTransitionFlag,
  setStoredSession,
  STORAGE_KEY,
} from './sessionStorage';
import {
  bootstrapRetryIntervalMs,
  bootstrapTimeoutMs,
  isIdentityMismatchError,
  isMissingUserIdentityError,
  isSessionTokenExpiredError,
  isUserNotFoundError,
  mergeSessionFallbacks,
  shouldPreserveSessionOnRefreshMiss,
  shouldPreserveSessionOnSignedOutSignal,
  type RefreshOutcome,
  wait,
} from './useAuth.helpers';
import { useAuthStateBypass } from './useAuthBypass';
import { useAuthClientBindings } from './useAuthClientBindings';
import type { StoredSession } from './sessionStorage';
import type {
  AuthError,
  AuthGuardState,
  AuthSession,
  SignInPayload,
  SignUpPayload,
  UseAuthResult,
} from './types';
import type { ReactElement, ReactNode } from 'react';

const useAuthState = (): UseAuthResult => {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();
  const { client } = useClerk();
  const [session, setSession] = useState<AuthSession | null>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const queryClient = useQueryClient();
  const refreshInFlight = useRef<Promise<RefreshOutcome> | null>(null);
  const authBootstrapOwnerIdRef = useRef(createAuthBootstrapOwnerId());
  const previousUserIdRef = useRef<string | null>(null);
  const clerkClientRef = useRef<unknown>(client as unknown);
  const clerkGetTokenRef = useRef(getToken);
  const clerkSignedInRef = useRef(isSignedIn);
  const hasSignedInSignal = resolveClerkSignedInSignal({
    isSignedIn,
    client: client as unknown,
  });
  clerkClientRef.current = client as unknown;
  clerkGetTokenRef.current = getToken;
  clerkSignedInRef.current = isSignedIn;

  const loadCurrentClerkSession = useCallback(
    async (input?: { forceTokenRefresh?: boolean }): Promise<AuthSession | null> => {
      const allowProvisioning =
        getClerkProvisioningFlag() ||
        (typeof window !== 'undefined' && window.location.pathname.startsWith('/onboarding'));
      return await loadCurrentClerkSessionFromState({
        client: clerkClientRef.current,
        getToken: clerkGetTokenRef.current,
        currentUserId: sessionRef.current?.userId ?? null,
        allowProvisioning,
        provisioningMode: getClerkProvisioningMode(),
        forceTokenRefresh: input?.forceTokenRefresh,
        bootstrapSession: authApi.bootstrapSession,
        clearProvisioningFlag: () => {
          setClerkProvisioningFlag(false);
        },
      });
    },
    [],
  );

  const clearAuthRecoveryFailures = useCallback((): void => undefined, []);

  const applySession = useCallback(
    (next: AuthSession | null, options?: { persist?: boolean }): void => {
      const persist = options?.persist !== false;
      setCacheNamespace(next?.userId ?? null);
      if (!next) {
        clearPostAuthRedirectAttempt();
      } else {
        setManualSignOutFlag(false);
        setSignOutTransitionFlag(false);
      }
      sessionRef.current = next;
      if (persist) {
        if (next) {
          setStoredSession(next);
        } else {
          setStoredSession(null);
        }
      }
      setSession(next);
    },
    [],
  );

  const clearClientState = useCallback(
    (options?: { preserveSessionStorageKey?: boolean }): void => {
      clearAuthClientState(queryClient, options);
    },
    [queryClient],
  );

  const performSignOut = useCallback(
    (clearSelections: boolean): void => {
      setClerkProvisioningFlag(false);
      if (clearSelections) {
        clearClientState();
      }
      applySession(null);
      setError(null);
      setLoading(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('flowqa-signout', { detail: { clearSelections } }));
      }
    },
    [applySession, clearClientState],
  );

  const signOut = useCallback(
    (options?: { redirect?: boolean; skipClerkSignOut?: boolean }): void => {
      const shouldRedirect = options?.redirect === true;
      const skipClerkSignOut = options?.skipClerkSignOut === true;
      setManualSignOutFlag(true);
      setSignOutTransitionFlag(true);
      clearSessionExpiredFlag();
      broadcastAuthEvent({ type: 'signout', at: Date.now() });
      performSignOut(true);
      if (skipClerkSignOut) {
        void authApi.signOut();
        return;
      }
      if (shouldRedirect) {
        void clerkSignOut({ redirectUrl: '/sign-in' });
        return;
      }
      void clerkSignOut();
    },
    [clerkSignOut, performSignOut],
  );

  const expireSession = useCallback(
    (reason: 'timer' | 'refresh_error' | 'session_expired_event'): void => {
      emitAuthDiagnostic('auth_expire_session', {
        reason,
        userId: sessionRef.current?.userId ?? null,
        orgId: sessionRef.current?.orgId ?? null,
        lastOrganisationId: sessionRef.current?.lastOrganisationId ?? null,
        lastProjectId: sessionRef.current?.lastProjectId ?? null,
      });
      traceAuthSessionExpired({
        userId: sessionRef.current?.userId ?? null,
        reason,
      });
      setSessionExpiredFlag(true);
      broadcastAuthEvent({ type: 'session-expired', at: Date.now() });
      performSignOut(true);
    },
    [performSignOut],
  );

  useEffect(() => {
    const currentUserId = session?.userId ?? null;
    const previousUserId = previousUserIdRef.current;
    if (
      previousUserId &&
      currentUserId &&
      previousUserId !== currentUserId &&
      !getE2eAccessToken()
    ) {
      clearClientState({ preserveSessionStorageKey: true });
      window.dispatchEvent(
        new CustomEvent('flowqa-signout', { detail: { clearSelections: true } }),
      );
    }
    previousUserIdRef.current = currentUserId;
  }, [clearClientState, session?.userId]);

  useEffect(() => {
    const parsed = getStoredSession();
    const e2eToken = getE2eAccessToken();
    if (e2eToken) {
      const storedAccessToken = parsed?.tokens?.accessToken;
      const hasLegacyCookieToken =
        typeof storedAccessToken === 'string' && isCookieAuthToken(storedAccessToken);
      if (parsed && typeof storedAccessToken === 'string' && !hasLegacyCookieToken) {
        applySession({
          ...parsed,
          orgId: parsed.orgId ?? undefined,
          orgName: parsed.orgName ?? undefined,
          tokens: { accessToken: storedAccessToken },
        });
      } else if (hasLegacyCookieToken) {
        setStoredSession(null);
        applySession(null);
      }
      setLoading(false);
      return;
    }
    const storedAccessToken = parsed?.tokens?.accessToken;
    const hasLegacyCookieToken =
      typeof storedAccessToken === 'string' && isCookieAuthToken(storedAccessToken);
    if (hasLegacyCookieToken) {
      setStoredSession(null);
    }
    const bootstrap = async (): Promise<void> => {
      let keepLoading = false;
      let bootstrapEventStartedAt: number | null = null;
      if (!isLoaded) {
        return;
      }
      if (getManualSignOutFlag()) {
        applySession(null);
        setLoading(false);
        return;
      }
      try {
        if (!hasSignedInSignal) {
          if (
            shouldPreserveSessionOnSignedOutSignal({
              hasSignedInSignal,
              currentSession: sessionRef.current,
            })
          ) {
            setLoading(false);
            return;
          }
          setManualSignOutFlag(false);
          setClerkProvisioningFlag(false);
          clearSessionExpiredFlag();
          applySession(null);
          setLoading(false);
          return;
        }
        const coordinatedBootstrapResult = await waitForCoordinatedAuthBootstrap(
          authBootstrapOwnerIdRef.current,
        );
        if (coordinatedBootstrapResult === 'succeeded') {
          await wait(50);
        }
        const startedAt = Date.now();
        bootstrapEventStartedAt = markAuthBootstrapStarted(authBootstrapOwnerIdRef.current);
        let refreshed: AuthSession | null = null;
        while (Date.now() - startedAt < bootstrapTimeoutMs) {
          refreshed = await loadCurrentClerkSession();
          if (refreshed) {
            break;
          }
          await wait(bootstrapRetryIntervalMs);
        }
        if (!refreshed) {
          clearAuthRecoveryFailures();
          setError({ code: 'AUTH_ERROR', message: 'Unable to load authentication session' });
          setClerkProvisioningFlag(false);
          clearSessionExpiredFlag();
          applySession(null);
          markAuthBootstrapFinished(authBootstrapOwnerIdRef.current, 'failed');
          keepLoading = false;
          return;
        }
        clearAuthRecoveryFailures();
        setError(null);
        clearSessionExpiredFlag();
        applySession(mergeSessionFallbacks(refreshed, sessionRef.current));
        markAuthBootstrapFinished(authBootstrapOwnerIdRef.current, 'succeeded');
      } catch (_err) {
        if (isIdentityMismatchError(_err)) {
          setError({
            code: 'AUTH_ERROR',
            message: 'Account identity changed. Please sign in again.',
          });
          setClerkProvisioningFlag(false);
          setManualSignOutFlag(true);
          applySession(null);
          markAuthBootstrapFinished(authBootstrapOwnerIdRef.current, 'failed');
          void clerkSignOut();
          keepLoading = false;
          return;
        }
        if (isMissingUserIdentityError(_err)) {
          setError({
            code: 'AUTH_ERROR',
            message: getMessage('auth.signIn.accountNotFound'),
          });
          setClerkProvisioningFlag(false);
          setManualSignOutFlag(true);
          applySession(null);
          markAuthBootstrapFinished(authBootstrapOwnerIdRef.current, 'failed');
          keepLoading = false;
          return;
        }
        if (isUserNotFoundError(_err)) {
          setError({
            code: 'AUTH_ERROR',
            message: getMessage('auth.signIn.accountNotFound'),
          });
          setClerkProvisioningFlag(false);
          setManualSignOutFlag(true);
          applySession(null);
          markAuthBootstrapFinished(authBootstrapOwnerIdRef.current, 'failed');
          keepLoading = false;
          return;
        }
        setError({ code: 'AUTH_ERROR', message: 'Unable to load authentication session' });
        setClerkProvisioningFlag(false);
        clearSessionExpiredFlag();
        applySession(null);
        markAuthBootstrapFinished(authBootstrapOwnerIdRef.current, 'failed');
        keepLoading = false;
      } finally {
        if (bootstrapEventStartedAt !== null) {
          clearOwnedAuthBootstrapEvent(authBootstrapOwnerIdRef.current, bootstrapEventStartedAt);
        }
        setLoading(!hasSignedInSignal ? false : keepLoading);
      }
    };
    void bootstrap();
  }, [
    applySession,
    clearClientState,
    expireSession,
    hasSignedInSignal,
    isLoaded,
    loadCurrentClerkSession,
  ]);

  const signIn = useCallback(async (input: SignInPayload): Promise<void> => {
    void input;
    await Promise.resolve();
    window.location.assign('/sign-in');
  }, []);

  const signUp = useCallback(async (input: SignUpPayload): Promise<void> => {
    void input;
    await Promise.resolve();
    window.location.assign('/sign-up');
  }, []);

  const runRefreshSession = useCallback(
    async (opts?: { force?: boolean }): Promise<RefreshOutcome> => {
      const forceRefresh = opts?.force === true;
      const currentSession = sessionRef.current;
      if (getE2eAccessToken()) {
        return { session: currentSession, didRefresh: false };
      }
      if (refreshInFlight.current) {
        return await refreshInFlight.current;
      }
      const promise = (async (): Promise<RefreshOutcome> => {
        try {
          const nextHasSignedInSignal = resolveClerkSignedInSignal({
            isSignedIn: clerkSignedInRef.current,
            client: clerkClientRef.current,
          });
          traceAuthRefreshStarted({
            userId: currentSession?.userId ?? null,
            forceRefresh,
            hasSignedInSignal: nextHasSignedInSignal,
            hasCurrentSession: currentSession !== null,
          });
          if (!nextHasSignedInSignal) {
            if (
              shouldPreserveSessionOnSignedOutSignal({
                hasSignedInSignal: nextHasSignedInSignal,
                currentSession,
              })
            ) {
              traceAuthRefreshCompleted({
                userId: currentSession?.userId ?? null,
                forceRefresh,
                outcome: 'preserved_signed_out_signal',
                didRefresh: false,
                expiresAt: currentSession?.expiresAt ?? null,
              });
              return { session: currentSession, didRefresh: false };
            }
            applySession(null);
            traceAuthRefreshCompleted({
              userId: currentSession?.userId ?? null,
              forceRefresh,
              outcome: 'cleared_signed_out_signal',
              didRefresh: false,
              expiresAt: null,
            });
            return { session: null, didRefresh: false };
          }
          const refreshed = await loadCurrentClerkSession({ forceTokenRefresh: forceRefresh });
          if (!refreshed) {
            if (
              shouldPreserveSessionOnRefreshMiss({ refreshedSession: refreshed, currentSession })
            ) {
              traceAuthRefreshCompleted({
                userId: currentSession?.userId ?? null,
                forceRefresh,
                outcome: 'preserved_refresh_miss',
                didRefresh: false,
                expiresAt: currentSession?.expiresAt ?? null,
              });
              return { session: currentSession, didRefresh: false };
            }
            applySession(null);
            traceAuthRefreshCompleted({
              userId: currentSession?.userId ?? null,
              forceRefresh,
              outcome: 'cleared_refresh_miss',
              didRefresh: false,
              expiresAt: null,
            });
            return { session: null, didRefresh: false };
          }
          setError(null);
          clearAuthRecoveryFailures();
          const merged = mergeSessionFallbacks(refreshed, sessionRef.current);
          applySession(merged);
          traceAuthRefreshCompleted({
            userId: merged.userId,
            forceRefresh,
            outcome: 'refreshed',
            didRefresh: true,
            expiresAt: merged.expiresAt ?? null,
          });
          return { session: merged, didRefresh: true };
        } catch (err) {
          const apiError = err instanceof ApiError ? err : null;
          traceAuthRefreshFailed({
            userId: currentSession?.userId ?? null,
            forceRefresh,
            status: apiError?.status ?? null,
            code: apiError?.code ?? null,
            error: err,
          });
          if (isIdentityMismatchError(err)) {
            emitAuthDiagnostic('auth_refresh_forced_expire', {
              branch: 'authoritative_auth_error',
              userId: currentSession?.userId ?? null,
              forceRefresh,
              status: apiError?.status ?? null,
              code: apiError?.code ?? null,
              message: apiError?.message ?? (err instanceof Error ? err.message : String(err)),
            });
            setError({ code: 'AUTH_ERROR', message: 'Unable to refresh authentication session' });
            traceAuthRefreshCompleted({
              userId: currentSession?.userId ?? null,
              forceRefresh,
              outcome: 'expired_auth_error',
              didRefresh: false,
              expiresAt: currentSession?.expiresAt ?? null,
            });
            expireSession('refresh_error');
            return { session: null, didRefresh: false };
          }
          if (isMissingUserIdentityError(err) || isUserNotFoundError(err)) {
            emitAuthDiagnostic('auth_refresh_missing_user_terminal', {
              userId: currentSession?.userId ?? null,
              forceRefresh,
              status: apiError?.status ?? null,
              code: apiError?.code ?? null,
              message: apiError?.message ?? (err instanceof Error ? err.message : String(err)),
            });
            setError({
              code: 'AUTH_ERROR',
              message: getMessage('auth.signIn.accountNotFound'),
            });
            setClerkProvisioningFlag(false);
            setManualSignOutFlag(true);
            applySession(null);
            traceAuthRefreshCompleted({
              userId: currentSession?.userId ?? null,
              forceRefresh,
              outcome: 'missing_user_terminal',
              didRefresh: false,
              expiresAt: null,
            });
            return { session: null, didRefresh: false };
          }
          if (err instanceof ApiError && (err.status === 429 || err.code === 'RATE_LIMITED')) {
            traceAuthRefreshCompleted({
              userId: currentSession?.userId ?? null,
              forceRefresh,
              outcome: 'rate_limited',
              didRefresh: false,
              expiresAt: currentSession?.expiresAt ?? null,
            });
            return { session: currentSession, didRefresh: false };
          }
          if (currentSession && err instanceof ApiError && isSessionTokenExpiredError(err)) {
            emitAuthDiagnostic('auth_refresh_forced_expire', {
              branch: 'expired_session_token',
              userId: currentSession.userId,
              forceRefresh,
              status: err.status ?? null,
              code: err.code ?? null,
              message: err.message,
              correlationId: err.correlationId ?? null,
              errorId: err.errorId ?? null,
            });
            setError({ code: 'AUTH_ERROR', message: 'Unable to refresh authentication session' });
            traceAuthRefreshCompleted({
              userId: currentSession.userId,
              forceRefresh,
              outcome: 'expired_auth_error',
              didRefresh: false,
              expiresAt: currentSession.expiresAt ?? null,
            });
            expireSession('refresh_error');
            return { session: null, didRefresh: false };
          }
          if (
            currentSession &&
            err instanceof ApiError &&
            (err.status === 401 || err.status === 403)
          ) {
            setError({ code: 'AUTH_ERROR', message: 'Unable to refresh authentication session' });
            traceAuthRefreshCompleted({
              userId: currentSession.userId,
              forceRefresh,
              outcome: 'preserved_auth_error',
              didRefresh: false,
              expiresAt: currentSession.expiresAt ?? null,
            });
            return { session: currentSession, didRefresh: false };
          }
          setError({ code: 'AUTH_ERROR', message: 'Unable to refresh authentication session' });
          traceAuthRefreshCompleted({
            userId: currentSession?.userId ?? null,
            forceRefresh,
            outcome: 'expired_unexpected_error',
            didRefresh: false,
            expiresAt: currentSession?.expiresAt ?? null,
          });
          emitAuthDiagnostic('auth_refresh_forced_expire', {
            branch: 'unexpected_error',
            userId: currentSession?.userId ?? null,
            forceRefresh,
            status: apiError?.status ?? null,
            code: apiError?.code ?? null,
            message: apiError?.message ?? (err instanceof Error ? err.message : String(err)),
          });
          expireSession('refresh_error');
          return { session: null, didRefresh: false };
        } finally {
          refreshInFlight.current = null;
        }
      })();
      refreshInFlight.current = promise;
      return await promise;
    },
    [applySession, clearAuthRecoveryFailures, expireSession, loadCurrentClerkSession],
  );

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const outcome = await runRefreshSession();
    return Boolean(outcome.session);
  }, [runRefreshSession]);
  const runRefreshSessionRef = useRef(runRefreshSession);

  useEffect(() => {
    runRefreshSessionRef.current = runRefreshSession;
  }, [runRefreshSession]);

  useAuthClientBindings({
    clerkClientRef,
    clerkGetTokenRef,
    clearAuthRecoveryFailures,
    expireSession,
    loading,
    runRefreshSessionRef,
    sessionRef,
  });

  useEffect(() => {
    if (session?.orgId && !session.orgName) {
      const orgId = session.orgId;
      if (!orgId) return;
      void (async () => {
        try {
          const organisation = await authApi.fetchOrganisation(orgId);
          applySession({ ...session, orgName: organisation.name });
        } catch {
          // ignore fetch failures; fallback will remain
        }
      })();
    }
  }, [applySession, session]);

  useEffect(() => {
    if (!session) return;
    const handleFocus = (): void => {
      void refreshSession();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshSession, session]);

  const guardState = useCallback((): AuthGuardState => {
    return { session, loading, error };
  }, [session, loading, error]);

  useEffect(() => {
    const handler = (event: StorageEvent): void => {
      if (event.newValue === event.oldValue) return;
      if (event.key === STORAGE_KEY) {
        if (!event.newValue) {
          applySession(null);
          return;
        }
        try {
          const parsed = JSON.parse(event.newValue) as StoredSession;
          const currentSession = sessionRef.current;
          if (!currentSession || parsed.userId !== currentSession.userId) {
            return;
          }
          applySession(
            {
              ...currentSession,
              orgId: parsed.orgId ?? currentSession.orgId,
              orgName: parsed.orgName ?? currentSession.orgName,
              lastOrganisationId: parsed.lastOrganisationId ?? currentSession.lastOrganisationId,
              lastProjectId: parsed.lastProjectId ?? currentSession.lastProjectId,
              onboardingCompleted: parsed.onboardingCompleted ?? currentSession.onboardingCompleted,
              emailVerified: parsed.emailVerified ?? currentSession.emailVerified,
              roles: parsed.roles ?? currentSession.roles,
              superuserEmailMatched:
                parsed.superuserEmailMatched ?? currentSession.superuserEmailMatched,
            },
            { persist: false },
          );
        } catch {
          // Ignore malformed cross-tab session metadata writes.
        }
        return;
      }
      const authEvent = parseAuthBroadcastEvent(
        event.key === AUTH_BROADCAST_KEY ? event.newValue : null,
      );
      if (!authEvent) return;
      if (authEvent.type === 'signout') {
        setManualSignOutFlag(true);
        setSignOutTransitionFlag(true);
      }
      performSignOut(true);
      void clerkSignOut();
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [applySession, clerkSignOut, performSignOut]);

  useEffect(() => {
    const handler = (): void => {
      if (getE2eAccessToken()) return;
      emitAuthDiagnostic('auth_session_expired_event_consumed', {
        userId: sessionRef.current?.userId ?? null,
        hasSession: sessionRef.current !== null,
      });
      expireSession('session_expired_event');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
    };
  }, [expireSession]);
  return {
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    refreshSession,
    guardState,
    setSessionDirect: applySession,
  };
};

const AuthProviderWithClerk = ({ children }: { children: ReactNode }): ReactElement =>
  createElement(AuthContext.Provider, { value: useAuthState() }, children);

const AuthProviderBypass = ({ children }: { children: ReactNode }): ReactElement =>
  createElement(AuthContext.Provider, { value: useAuthStateBypass() }, children);

export const AuthProvider = ({ children }: { children: ReactNode }): ReactElement => {
  if (authBypassClerkEnabled) {
    return createElement(AuthProviderBypass, { children });
  }
  return createElement(AuthProviderWithClerk, { children });
};

export const useAuth = (): UseAuthResult => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export {
  shouldPreserveSessionOnRefreshMiss,
  shouldPreserveSessionOnSignedOutSignal,
} from './useAuth.helpers';
